import {
  IElement,
  OneOfChildElements,
  OneOfElements,
  OneOfListElements,
} from '../models/project.model';
import { ResultsRecord } from '../models/unit.model';
import {
  IProductElementParentItem,
  ProductRecord,
} from '../models/product.model';
import {
  getChildElements,
  isBuildingVersionElement,
  isElement,
} from './recursive-element.helpers';
import { getElementName } from './element.helpers';
import { cloneMoveAfter, cloneMoveBefore, sort } from './array.helpers';
import {
  SortOptions,
  ElementSortFn,
  IInsertSortPlacement,
  SortByAlternative,
} from '../models/sort.model';
import { getConversionFactorValue } from './conversion-factors.helpers';

export const getElementSortFunction = <T extends OneOfListElements>({
  sortBy,
  productLookup,
  resultsRecord = {},
}: SortOptions): ElementSortFn<T> => {
  switch (sortBy) {
    case 'name': {
      return (a, b) => {
        return getElementName(a, productLookup).toLocaleLowerCase() >
          getElementName(b, productLookup).toLocaleLowerCase()
          ? 1
          : -1;
      };
    }
    case 'co2': {
      return (a, b) =>
        getConversionFactorValue(resultsRecord[b.id] ?? {}, 'co2e') -
        getConversionFactorValue(resultsRecord[a.id] ?? {}, 'co2e');
    }
    case 'cost': {
      return (a, b) =>
        getConversionFactorValue(resultsRecord[b.id] ?? {}, 'sek') -
        getConversionFactorValue(resultsRecord[a.id] ?? {}, 'sek');
    }
    case 'position': {
      // Function that doesn't change existing order
      return noSortFn;
    }
    default:
      throw new Error('Unknown element sort function requested.');
  }
};

const noSortFn = () => 0;

const getSortOptions = (
  options: SortOptions,
): SortOptions & { resultsRecord?: ResultsRecord } => {
  if (options.sortBy === 'name') {
    return { sortBy: 'name', productLookup: options.productLookup };
  }
  if (options.sortBy === 'co2' || options.sortBy === 'cost') {
    return {
      sortBy: options.sortBy,
      productLookup: options.productLookup,
      resultsRecord: options.resultsRecord,
    };
  }
  return { sortBy: 'position' };
};

export const sortElements = <T extends OneOfListElements = IElement>(
  elements: T[],
  options: SortOptions = { sortBy: 'position' },
): T[] => {
  // create copy of elements to avoid mutation of state and to keep the order of the original list
  const newList = [...elements];
  const sortOptions = getSortOptions(options);

  return sort<T>(newList, getElementSortFunction(sortOptions));
};

/**
 * Get all elements with children in a flat array.
 * Will be in the order of the element list
 */
export const flattenSortElements = <T extends OneOfElements>(
  sortBy: SortByAlternative,
  productLookup: ProductRecord,
  ...elements: T[]
): (T | OneOfChildElements)[] => {
  const sortedElements = sortElements(elements, {
    sortBy,
    productLookup,
  });

  const elementsWithChildren = sortedElements.map((element) => [
    element,
    ...flattenSortElements(sortBy, productLookup, ...getChildElements(element)),
  ]);

  return elementsWithChildren.flat();
};

export const sortInsert = <T>(
  placement: IInsertSortPlacement,
  elements: T[],
  adjacentElement: T | undefined,
  ...elementsToInsert: T[]
) => {
  if (placement === 'first') {
    return [...elementsToInsert, ...elements];
  }
  if (placement === 'last') {
    return [...elements, ...elementsToInsert];
  }
  if (adjacentElement) {
    if (placement === 'before') {
      return cloneMoveBefore(elements, adjacentElement, ...elementsToInsert);
    }
    if (placement === 'after') {
      return cloneMoveAfter(elements, adjacentElement, ...elementsToInsert);
    }
  }
  return [...elementsToInsert, ...elements];
};

const getKindSortValue = <T extends OneOfListElements>(element: T): number => {
  if (isBuildingVersionElement(element)) {
    return 2;
  } else if (isElement(element)) {
    return 1;
  }
  return 0;
};

export const sortElementsByKind = <T extends OneOfListElements>(
  a: T,
  b: T,
  fallback?: ElementSortFn<T>,
) => {
  const byKind = getKindSortValue(b) - getKindSortValue(a);

  if (byKind === 0 && fallback) {
    return fallback(a, b);
  }
  return byKind;
};

export const sortProductsByCO2 = (
  a: IProductElementParentItem,
  b: IProductElementParentItem,
): number => {
  return b.co2eSum - a.co2eSum;
};

/**
 * Sort entries by value descending
 * @param valueA - The first value
 * @param valueB - The second value
 * @returns The sorted entries
 */
export const entrySortFn = (
  [, valueA = Number.MIN_VALUE]: [unknown, number | undefined],
  [, valueB = Number.MIN_VALUE]: [unknown, number | undefined],
) => valueB - valueA;

const BEGIN_DATE = '1971-05-22T10:33:14.183Z';

type UpdatedAt = { updated_at?: string };

export const sortByLatestUpdatedAtFn = (
  { updated_at: updatedAtA = BEGIN_DATE }: UpdatedAt,
  { updated_at: updatedAtB = BEGIN_DATE }: UpdatedAt,
) => {
  if (updatedAtA === updatedAtB) {
    return 0;
  }
  return updatedAtA < updatedAtB ? 1 : -1;
};
